import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Menu,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  IconChevronDown,
  IconClipboardList,
  IconDownload,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@/lib/icons";
import { ArrowRight, Activity, FileSearch, User as UserIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import {
  type ExaminationRead,
  bulkDeleteExaminations,
  bulkDownloadFormsBlob,
  cancelExamination,
  getProfpatologQueue,
  getWorklist,
  listExaminations,
} from "@/lib/api/examinations";
import { getPatient, listPatients, type PatientRead } from "@/lib/api/patients";
import { formatDate, formatDateTime } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

import { OpenExamDialog } from "@/features/examinations/OpenExamDialog";

const ADMIN_ROLES = new Set(["admin", "registrar", "chief_doctor"]);

export function ExaminationListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdminLike =
    user?.role === "admin" || user?.role === "registrar" ||
    user?.role === "chief_doctor";
  // The "ready / completed" queue view belongs to the medical commission only
  // (they're the ones who issue verdicts and finalize cases).
  const isCommission = user?.role === "medical_commission";
  const isDoctorOrLab = user?.role === "doctor" || user?.role === "lab_tech";
  const canCreate = useAuthStore((s) => s.hasPermission("examination.create"));
  const canDelete = useAuthStore((s) => s.hasPermission("examination.delete"));

  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 300);
  const [selected, setSelected] = useState<string[]>([]);
  const [newOpen, newHandlers] = useDisclosure(false);
  const [newPatientId, setNewPatientId] = useState<string | undefined>(undefined);

  // Auto-open the "Yangi ko'rik" dialog when redirected from /patients/new
  useEffect(() => {
    const newFor = searchParams.get("new_for");
    if (newFor) {
      setNewPatientId(newFor);
      newHandlers.open();
      const next = new URLSearchParams(searchParams);
      next.delete("new_for");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cancelling, setCancelling] = useState<ExaminationRead | null>(null);
  const [cancelOpen, cancelHandlers] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState("");

  // Admin-like view: full list
  const adminQuery = useQuery({
    queryKey: ["examinations", debSearch],
    queryFn: () => listExaminations({ limit: 200 }),
    enabled: isAdminLike,
  });

  // Patients (for "no exam yet" rows in admin view)
  const allPatientsQuery = useQuery({
    queryKey: ["patients-all", debSearch],
    queryFn: () => listPatients({ limit: 200, q: debSearch || undefined }),
    enabled: isAdminLike,
  });

  // Profpatolog (chief): ready + completed (used in addition to admin if chief_doctor)
  const queueQuery = useQuery({
    queryKey: ["profpatolog-queue"],
    queryFn: getProfpatologQueue,
    enabled: isCommission,
  });

  // Doctor/lab: own worklist
  const worklistQuery = useQuery({
    queryKey: ["worklist", "all"],
    // Show both open AND completed sections so doctors can re-edit their work
    queryFn: () => getWorklist(false),
    enabled: isDoctorOrLab,
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      cancelExamination(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["examinations"] });
      queryClient.invalidateQueries({ queryKey: ["profpatolog-queue"] });
      notifications.show({ color: "green", message: t("examinations.cancelled") });
      cancelHandlers.close();
      setCancelling(null);
      setCancelReason("");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteExaminations,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["examinations"] });
      queryClient.invalidateQueries({ queryKey: ["profpatolog-queue"] });
      notifications.show({
        color: "green",
        message: `${data.deleted} ${t("examinations.bulk_deleted")}`,
      });
      setSelected([]);
    },
  });

  const [downloadOpen, downloadHandlers] = useDisclosure(false);
  const [downloadTemplate, setDownloadTemplate] = useState<
    "form_086" | "form_083" | "form_089"
  >("form_086");

  const bulkDownloadMutation = useMutation({
    mutationFn: ({ ids, template }: { ids: string[]; template: "form_086" | "form_083" | "form_089" }) =>
      bulkDownloadFormsBlob(ids, template, "uz"),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notifications.show({
        color: "green",
        message: t("examinations.bulk_downloaded"),
      });
      downloadHandlers.close();
    },
    onError: () => {
      notifications.show({
        color: "red",
        message: t("examinations.bulk_download_failed"),
      });
    },
  });

  const allItems: ExaminationRead[] = useMemo(() => {
    if (isCommission && queueQuery.data) {
      return [...queueQuery.data.ready, ...queueQuery.data.completed];
    }
    if (isAdminLike) return adminQuery.data?.items ?? [];
    return [];
  }, [isAdminLike, isCommission, adminQuery.data, queueQuery.data]);

  // Fetch patients for visible exams
  const patientIds = useMemo(
    () => Array.from(new Set(allItems.map((e) => e.patient_id))),
    [allItems],
  );
  const patientQueries = useQueries({
    queries: patientIds.map((pid) => ({
      queryKey: ["patient", pid],
      queryFn: () => getPatient(pid),
      staleTime: 60_000,
    })),
  });
  const patientById = new Map<string, { name: string; card: string }>();
  patientQueries.forEach((q, i) => {
    if (q.data) {
      patientById.set(patientIds[i], {
        name: `${q.data.family_name} ${q.data.given_name}`,
        card: q.data.card_number,
      });
    }
  });

  const filterBySearch = (items: ExaminationRead[]) => {
    if (!debSearch.trim()) return items;
    const q = debSearch.trim().toLowerCase();
    return items.filter((e) => {
      const p = patientById.get(e.patient_id);
      return (
        (p?.name ?? "").toLowerCase().includes(q) ||
        (p?.card ?? "").toLowerCase().includes(q) ||
        (e.job_title ?? "").toLowerCase().includes(q)
      );
    });
  };

  const toggleAll = (visible: ExaminationRead[]) => {
    const ids = visible.map((e) => e.id);
    if (selected.length === ids.length) setSelected([]);
    else setSelected(ids);
  };

  const renderRow = (e: ExaminationRead, opts: { showCheckbox?: boolean } = {}) => {
    const patient = patientById.get(e.patient_id);
    const initials = patient?.name
      .split(" ")
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
    return (
      <Table.Tr key={e.id}>
        {opts.showCheckbox && canDelete && (
          <Table.Td style={{ width: 40 }}>
            <Checkbox
              checked={selected.includes(e.id)}
              onChange={() =>
                setSelected((prev) =>
                  prev.includes(e.id) ? prev.filter((x) => x !== e.id) : [...prev, e.id],
                )
              }
            />
          </Table.Td>
        )}
        <Table.Td>
          <Text ff="monospace" fw={700} c="brand.7" size="sm">
            #{e.exam_number || "—"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap="sm" wrap="nowrap">
            <Avatar
              size={32}
              radius="md"
              variant="gradient"
              gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
            >
              <Text size="xs" fw={700} c="white">
                {initials || <UserIcon size={14} strokeWidth={2} />}
              </Text>
            </Avatar>
            <Stack gap={0}>
              <Text size="sm" fw={600}>
                {patient?.name ?? "—"}
              </Text>
              <Text size="xs" c="dimmed" ff="monospace">
                {patient?.card ?? "—"}
              </Text>
            </Stack>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{e.job_title ?? "—"}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed">
            {formatDate(e.created_at)}
          </Text>
        </Table.Td>
        <Table.Td>
          <StatusBadge status={e.status} i18nPrefix="examinations.statuses" />
        </Table.Td>
        <Table.Td>
          <Group gap="xs" justify="flex-end">
            <Button
              size="xs"
              variant="light"
              radius="xl"
              rightSection={<ArrowRight size={14} strokeWidth={2.4} />}
              onClick={() => navigate(`/examinations/${e.id}`)}
            >
              {t("common.details")}
            </Button>
            {canDelete && e.status !== "completed" && e.status !== "cancelled" && (
              <Menu position="bottom-end" shadow="md">
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" radius="xl">
                    <IconChevronDown size={16} strokeWidth={2} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconTrash size={14} strokeWidth={2.2} />}
                    color="red"
                    onClick={() => {
                      setCancelling(e);
                      setCancelReason("");
                      cancelHandlers.open();
                    }}
                  >
                    {t("examinations.cancel")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = (
    rows: ExaminationRead[],
    opts: { showCheckbox?: boolean; loading?: boolean } = {},
  ) => {
    const visibleSelected = rows.filter((r) => selected.includes(r.id)).length;
    const cols = (opts.showCheckbox && canDelete ? 1 : 0) + 6;
    return (
      <Table.ScrollContainer minWidth={900}>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {opts.showCheckbox && canDelete && (
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    checked={rows.length > 0 && visibleSelected === rows.length}
                    indeterminate={visibleSelected > 0 && visibleSelected < rows.length}
                    onChange={() => toggleAll(rows)}
                  />
                </Table.Th>
              )}
              <Table.Th style={{ width: 90 }}>ID</Table.Th>
              <Table.Th>{t("examinations.fields.patient")}</Table.Th>
              <Table.Th>{t("examinations.fields.job_title")}</Table.Th>
              <Table.Th>{t("examinations.fields.created_at")}</Table.Th>
              <Table.Th>{t("examinations.fields.status")}</Table.Th>
              <Table.Th ta="right">{t("examinations.fields.actions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {opts.loading && (
              <Table.Tr>
                <Table.Td colSpan={cols}>
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                  </Group>
                </Table.Td>
              </Table.Tr>
            )}
            {!opts.loading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={cols}>
                  <EmptyState
                    icon={<IconClipboardList size={32} strokeWidth={1.5} />}
                    title={t("examinations.empty")}
                  />
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((e) => renderRow(e, { showCheckbox: opts.showCheckbox }))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    );
  };

  // Render: doctor/lab worklist
  if (isDoctorOrLab) {
    const items = worklistQuery.data?.items ?? [];
    const filtered = !debSearch
      ? items
      : items.filter((it) =>
          (it.patient_name + " " + it.patient_card_number)
            .toLowerCase()
            .includes(debSearch.toLowerCase()),
        );
    return (
      <Stack gap="lg">
        <PageHeader
          title={t("examinations.my_worklist_title")}
          subtitle={t("examinations.my_worklist_subtitle")}
        />

        <Card padding="md">
          <TextInput
            rightSection={<IconSearch size={18} strokeWidth={2} />}
            placeholder={t("examinations.search_my_tasks")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </Card>

        <Card padding={0} radius="xl">
          <Table.ScrollContainer minWidth={780}>
            <Table verticalSpacing="md" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("examinations.fields.patient")}</Table.Th>
                  <Table.Th>{t("examinations.fields.type")}</Table.Th>
                  <Table.Th>{t("examinations.fields.created_at")}</Table.Th>
                  <Table.Th>{t("examinations.fields.section_status")}</Table.Th>
                  <Table.Th ta="right">{t("examinations.fields.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {worklistQuery.isLoading && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Group justify="center" py="md">
                        <Loader size="sm" />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
                {!worklistQuery.isLoading && filtered.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <EmptyState
                        icon={<Activity size={32} strokeWidth={1.5} />}
                        title={t("examinations.no_tasks")}
                      />
                    </Table.Td>
                  </Table.Tr>
                )}
                {filtered.map((it) => (
                  <Table.Tr key={it.section_id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" fw={600}>
                          {it.patient_name}
                        </Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {it.patient_card_number}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="brand" radius="sm">
                        {t(`examinations.types.${it.examination_type}`)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {formatDate(it.created_at)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={it.completed_at ? "brand" : "orange"}
                        radius="sm"
                      >
                        {it.completed_at
                          ? t("examinations.statuses.completed")
                          : t("examinations.statuses.pending")}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="flex-end">
                        <Button
                          size="xs"
                          variant="light"
                          radius="xl"
                          component={Link}
                          to={(() => {
                            const sp =
                              user?.specialty ||
                              (user?.role === "lab_tech" ? "laboratory" : "");
                            return sp
                              ? `/examinations/${it.examination_id}/sections/${sp}`
                              : `/examinations/${it.examination_id}`;
                          })()}
                          rightSection={<ArrowRight size={14} strokeWidth={2.4} />}
                        >
                          {it.completed_at ? t("common.edit") : t("common.details")}
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      </Stack>
    );
  }

  // Profpatolog: 2 tabs  ready / completed (from queue endpoint)
  if (isCommission) {
    const ready = filterBySearch(queueQuery.data?.ready ?? []);
    const completed = filterBySearch(queueQuery.data?.completed ?? []);
    return (
      <Stack gap="lg">
        <PageHeader
          title={t("examinations.profpatolog_title")}
          subtitle={t("examinations.profpatolog_subtitle")}
          actions={
            canCreate ? (
              <Button
                leftSection={<IconPlus size={16} strokeWidth={2.4} />}
                onClick={newHandlers.open}
                radius="xl"
              >
                {t("examinations.new")}
              </Button>
            ) : undefined
          }
        />

        <Card padding="md">
          <TextInput
            rightSection={<IconSearch size={18} strokeWidth={2} />}
            placeholder={t("examinations.search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </Card>

        <Card padding={0} radius="xl">
          <Group p="lg" gap="sm">
            <ThemeIcon size={32} radius="xl" variant="light" color="orange">
              <FileSearch size={16} strokeWidth={2} />
            </ThemeIcon>
            <Text fw={700} tt="uppercase" size="xs" c="dimmed" style={{ letterSpacing: "0.06em" }}>
              {t("examinations.tabs.ready")}
            </Text>
            <Badge variant="light" color="orange" radius="xl" size="sm">
              {ready.length}
            </Badge>
          </Group>
          {renderTable(ready, { loading: queueQuery.isLoading })}
        </Card>

        <Card padding={0} radius="xl">
          <Group p="lg" gap="sm">
            <ThemeIcon size={32} radius="xl" variant="light" color="brand">
              <IconClipboardList size={16} strokeWidth={2} />
            </ThemeIcon>
            <Text fw={700} tt="uppercase" size="xs" c="dimmed" style={{ letterSpacing: "0.06em" }}>
              {t("examinations.tabs.completed")}
            </Text>
            <Badge variant="light" color="brand" radius="xl" size="sm">
              {completed.length}
            </Badge>
          </Group>
          {renderTable(completed, { loading: queueQuery.isLoading })}
        </Card>

        <OpenExamDialog
        opened={newOpen}
        onClose={() => {
          newHandlers.close();
          setNewPatientId(undefined);
        }}
        patientId={newPatientId}
      />
      </Stack>
    );
  }

  // Admin / Registrar / default — full table with bulk actions
  const items = filterBySearch(adminQuery.data?.items ?? []);

  // Patients who don't yet have any examination
  const patientsWithExam = new Set(
    (adminQuery.data?.items ?? []).map((e) => e.patient_id),
  );
  const allPatients = allPatientsQuery.data?.items ?? [];
  const patientsWithoutExam = allPatients.filter(
    (p) => !patientsWithExam.has(p.id),
  );
  const filteredNoExam = !debSearch
    ? patientsWithoutExam
    : patientsWithoutExam.filter((p) => {
        const q = debSearch.toLowerCase();
        return (
          (p.family_name + " " + p.given_name).toLowerCase().includes(q) ||
          p.card_number.toLowerCase().includes(q) ||
          (p.phone_primary ?? "").toLowerCase().includes(q)
        );
      });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("examinations.title")}
        subtitle={adminQuery.data ? `${adminQuery.data.total}` : undefined}
        actions={
          canCreate ? (
            <Button
              leftSection={<IconPlus size={16} strokeWidth={2.4} />}
              onClick={newHandlers.open}
              radius="xl"
            >
              {t("examinations.new")}
            </Button>
          ) : undefined
        }
      />

      <Card padding="md">
        <Group justify="space-between" wrap="wrap">
          <TextInput
            rightSection={<IconSearch size={18} strokeWidth={2} />}
            placeholder={t("examinations.search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 280 }}
          />
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {t("examinations.selected")}: {selected.length}
            </Text>
            <Button
              variant="light"
              color="brand"
              leftSection={<IconDownload size={16} strokeWidth={2.2} />}
              loading={bulkDownloadMutation.isPending}
              disabled={selected.length === 0}
              onClick={() => downloadHandlers.open()}
            >
              {t("examinations.bulk_download")}
            </Button>
            {canDelete && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} strokeWidth={2.2} />}
                loading={bulkDeleteMutation.isPending}
                disabled={selected.length === 0}
                onClick={() => {
                  if (window.confirm(t("examinations.bulk_delete_confirm", { count: selected.length }))) {
                    bulkDeleteMutation.mutate(selected);
                  }
                }}
              >
                {t("examinations.bulk_delete")}
              </Button>
            )}
          </Group>
        </Group>
      </Card>

      {filteredNoExam.length > 0 && (
        <Card padding={0} radius="xl">
          <Group p="lg" gap="sm" justify="space-between">
            <Group gap="sm">
              <ThemeIcon size={32} radius="xl" variant="light" color="orange">
                <UserIcon size={16} strokeWidth={2} />
              </ThemeIcon>
              <Text fw={700} tt="uppercase" size="xs" c="dimmed" style={{ letterSpacing: "0.06em" }}>
                {t("examinations.no_exam_yet_title")}
              </Text>
              <Badge variant="light" color="orange" radius="xl" size="sm">
                {filteredNoExam.length}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {t("examinations.no_exam_yet_hint")}
            </Text>
          </Group>
          <Table.ScrollContainer minWidth={780}>
            <Table verticalSpacing="md" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("examinations.fields.patient")}</Table.Th>
                  <Table.Th>{t("patients.fields.phone")}</Table.Th>
                  <Table.Th>{t("patients.fields.birth_date")}</Table.Th>
                  <Table.Th>{t("examinations.fields.created_at")}</Table.Th>
                  <Table.Th ta="right">{t("examinations.fields.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredNoExam.map((p: PatientRead) => {
                  const initials = `${p.family_name} ${p.given_name}`
                    .split(" ")
                    .map((s) => s[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                  return (
                    <Table.Tr key={p.id}>
                      <Table.Td>
                        <Group gap="sm" wrap="nowrap">
                          <Avatar
                            size={32}
                            radius="md"
                            variant="gradient"
                            gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
                          >
                            <Text size="xs" fw={700} c="white">
                              {initials || <UserIcon size={14} strokeWidth={2} />}
                            </Text>
                          </Avatar>
                          <Stack gap={0}>
                            <Text size="sm" fw={600}>
                              {p.family_name} {p.given_name}
                            </Text>
                            <Text size="xs" c="dimmed" ff="monospace">
                              {p.card_number}
                            </Text>
                          </Stack>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {p.phone_primary ?? "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {p.birth_date}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {formatDate(p.created_at)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end">
                          <Button
                            size="xs"
                            variant="light"
                            color="brand"
                            radius="xl"
                            leftSection={<IconPlus size={14} strokeWidth={2.4} />}
                            onClick={() => {
                              setNewPatientId(p.id);
                              newHandlers.open();
                            }}
                          >
                            {t("examinations.open_action")}
                          </Button>
                          <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            component={Link}
                            to={`/patients/${p.id}`}
                          >
                            {t("common.details")}
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Card padding={0} radius="xl">
        {renderTable(items, { showCheckbox: true, loading: adminQuery.isLoading })}
      </Card>

      <OpenExamDialog
        opened={newOpen}
        onClose={() => {
          newHandlers.close();
          setNewPatientId(undefined);
        }}
        patientId={newPatientId}
      />

      <Modal
        opened={downloadOpen}
        onClose={downloadHandlers.close}
        title={t("examinations.bulk_download_title")}
        size="md"
        radius="lg"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {t("examinations.bulk_download_hint", { count: selected.length })}
          </Text>
          <Stack gap="xs">
            {(["form_086", "form_083", "form_089"] as const).map((tpl) => (
              <Button
                key={tpl}
                fullWidth
                size="md"
                radius="lg"
                variant={downloadTemplate === tpl ? "filled" : "light"}
                color="brand"
                justify="flex-start"
                onClick={() => setDownloadTemplate(tpl)}
              >
                {t(`examinations.types.${tpl}`)}
              </Button>
            ))}
          </Stack>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={downloadHandlers.close}>
              {t("common.cancel")}
            </Button>
            <Button
              color="brand"
              loading={bulkDownloadMutation.isPending}
              leftSection={<IconDownload size={16} strokeWidth={2.2} />}
              onClick={() =>
                bulkDownloadMutation.mutate({ ids: selected, template: downloadTemplate })
              }
            >
              {t("examinations.bulk_download")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={cancelOpen}
        onClose={() => {
          cancelHandlers.close();
          setCancelling(null);
        }}
        title={t("examinations.cancel_title")}
        size="md"
        radius="lg"
      >
        <Stack>
          <Text size="sm">{t("examinations.cancel_confirm")}</Text>
          <Textarea
            label={t("examinations.cancel_reason")}
            placeholder={t("examinations.cancel_reason_placeholder")}
            minRows={3}
            autosize
            required
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={cancelHandlers.close}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={cancelMutation.isPending}
              disabled={cancelReason.trim().length < 10}
              onClick={() =>
                cancelling &&
                cancelMutation.mutate({ id: cancelling.id, reason: cancelReason.trim() })
              }
            >
              {t("examinations.cancel_confirm_button")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
